import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  viewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, map } from 'rxjs/operators';
import { UserDto } from 'src/app/api/platform';

import { AppService } from '../../services/app.service';
import { UsersService } from '../../services/users.service';

import { ItemActionsContentDirective } from '../../components/list-view/item-actions-content.directive';
import { ListViewComponent } from '../../components/list-view/list-view.component';

import {
  ListViewResults,
  ListViewSettings,
  ListViewTableColumnType,
} from '../../interfaces/list-view-models';
import { ServerResponse } from '../../interfaces/server-response';
import { ConnectedUserModel } from '../../interfaces/users-models';
import { AddAdminUserModalComponent } from '../../modals/add-admin-user-modal/add-admin-user-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ItemActionsContentDirective, ListViewComponent],
  selector: 'app-admin-users',
  standalone: true,
  templateUrl: './admin-users.component.html',
})
export class AdminUsersComponent {
  appService: AppService;
  connectedUser: ConnectedUserModel | null;
  listViewSettings: ListViewSettings = {
    allowCreate: true,
    allowEdit: false,
    allowGalleryMode: false,
    create: () => this.create(),
    createButtonText: 'Add Admin User',
    defaultSortColumn: 'id',
    getData: () => this.getData(),
    hideSearch: true,
    isGalleryDefault: false,
    syncIntegrationData: () => {},
    tableSettings: {
      columns: [
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Id',
          name: 'id',
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Display Name',
          name: 'displayName',
        },
        {
          allowSort: false,
          columnType: ListViewTableColumnType.Text,
          displayText: 'Email',
          name: 'email',
        },
      ],
      showExpand: false,
    },
  };
  modalService: NgbModal;
  toastr: ToastrService;
  usersService: UsersService;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly listView = viewChild(ListViewComponent);

  constructor() {
    this.usersService = inject(UsersService);
    this.appService = inject(AppService);
    this.modalService = inject(NgbModal);
    this.toastr = inject(ToastrService);
    this.connectedUser = this.usersService.getConnectedUserSync();
  }

  removeUser(user: UserDto): void {
    this.appService
      .showConfirm('Confirm Delete', 'Are you sure you want to remove this user?')
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.usersService.removeUserAsSuperAdmin(user.id).subscribe({
          error: (err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            const detail = message ? ` ${message}` : '';
            this.toastr.error(`Failed to remove the user.${detail}`);
            this.cdr.markForCheck();
          },
          next: () => {
            this.listView()?.refreshData();
            this.cdr.markForCheck();
          },
        });
      });
  }

  private create(): Observable<boolean> {
    const createResult = new Subject<boolean>();

    const modal = this.modalService.open(AddAdminUserModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      size: 'lg',
      windowClass: 'dark-modal',
    });

    void modal.result
      .then(
        () => {
          createResult.next(true);
          return true;
        },
        () => {
          createResult.next(false);
          return false;
        }
      )
      .catch((error: unknown) => {
        createResult.next(false);
        console.error('Error creating admin user:', error);
      })
      .finally(() => {
        createResult.complete();
      });

    return createResult.asObservable();
  }

  private getData(): Observable<ServerResponse<ListViewResults<UserDto>>> {
    return this.usersService.getAllSuperAdmins().pipe(
      defaultIfEmpty([]),
      map((users: UserDto[] | null | undefined) => {
        const list = Array.isArray(users) ? users : [];
        return {
          data: {
            data: list,
            dataCount: list.length,
          },
          result: true,
        } satisfies ServerResponse<ListViewResults<UserDto>>;
      }),
      catchError(() => {
        this.toastr.error('Failed to load admin users. Please try again.');
        return of({
          data: { data: [], dataCount: 0 },
          result: false,
        } satisfies ServerResponse<ListViewResults<UserDto>>);
      })
    );
  }
}
